import { FormEvent, useState, useEffect, useRef, KeyboardEvent, ClipboardEvent, ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneIcon, CheckCircle2, ChevronDown, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { logUserLogin } from "@/utils/analyticsUtils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel, 
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PHONE_PREFIXES } from "@/constants/phone";
import { useTranslation } from "react-i18next";
// import { Turnstile } from '@marsidev/react-turnstile'
import { checkOTPRateLimit, recordOTPRequest } from "@/utils/otpRateLimit";

type PhoneSignInFormProps = {
  redirectLoggedInUser: (userId: string) => Promise<void>;
};

export const PhoneSignInForm = ({ redirectLoggedInUser }: PhoneSignInFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [phonePrefix, setPhonePrefix] = useState("+254"); // Default to Kenya, but user can change
  const [verificationCode, setVerificationCode] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [verificationSent, setVerificationSent] = useState(false);
  const [temporaryPhone, setTemporaryPhone] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // For searching country codes
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // const [captchaToken, setCaptchaToken] = useState();
  // const [turnstileKey, setTurnstileKey] = useState(0);
  
  // const devSiteKey  = "1x00000000000000000000AA";
  // const prodSiteKey = "0x4AAAAAABipt3BbaWslaZ-z";

  // let siteKey = "";
  // if (!window.location.hostname.includes("jitume.gebeya.com")) {
  //   siteKey = devSiteKey;
  // } else {
  //   siteKey = prodSiteKey; 
  // }

  // const resetTurnstile = () => {
  //   setCaptchaToken(undefined);
  //   setTurnstileKey(prev => prev + 1);
  // };

  // Filter phone prefixes based on search query
  const filteredPrefixes = searchQuery
    ? PHONE_PREFIXES.filter(prefix => 
        prefix.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : [...PHONE_PREFIXES].sort((a, b) => {
        // Sort by priority countries first, then alphabetically
        const priorityCountries = ["Kenya", "Ethiopia", "Senegal", "Rwanda", "Nigeria", "South Africa"];
        
        // Check if either is a priority country
        const aIsPriority = priorityCountries.some(country => a.label.includes(country));
        const bIsPriority = priorityCountries.some(country => b.label.includes(country));
        
        if (aIsPriority && !bIsPriority) return -1;
        if (!aIsPriority && bIsPriority) return 1;
        
        // If both are priority or both are not, sort alphabetically
        return a.label.localeCompare(b.label);
      });

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          console.log("Auth state change: SIGNED_IN", session.user);
          
          // Check if this is from our phone verification flow
          if (temporaryPhone && temporaryPhone === session.user.phone) {
            console.log("Phone verification successful for", temporaryPhone);
            
            try {
              // Show success toast
              toast({
                title: "Welcome!",
                description: "You've successfully signed in.",
              });
              
              // Log the successful login
              await logUserLogin(session.user.id);
              
              // Redirect user
              try {
                await redirectLoggedInUser(session.user.id);
              } catch (error) {
                console.error("Error redirecting after login:", error);
                // Fallback navigation if profile check fails
                navigate('/dashboard', { replace: true }); 
              }
            } catch (error) {
              console.error("Error handling successful verification:", error);
            }
          }
        }
      }
    );
    
    return () => {
      subscription.unsubscribe();
    };
  }, [temporaryPhone, navigate, toast, redirectLoggedInUser]);

  // Send OTP
  const handleSendOTP = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!phone.trim()) {
      toast({
        variant: "destructive",
        title: t('auth.phoneNumberRequired'),
        description: t('auth.enterValidPhoneNumber'),
      });
      return;
    }
    
    // Check OTP rate limit before sending
    const rateLimitResult = checkOTPRateLimit(t);
    if (!rateLimitResult.allowed) {
      toast({
        variant: "destructive",
        title: t('auth.otpRateLimit.rateLimitExceeded'),
        description: rateLimitResult.message,
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Format the phone number to ensure it has country code
      const formattedPhone = formatPhoneNumber(phone, phonePrefix);
      setTemporaryPhone(formattedPhone); // Save for verification comparison
      
      console.log("Attempting to send SMS OTP to phone:", formattedPhone);
      
      const { data, error } = await supabase.auth.signInWithOtp({
        phone: formattedPhone,
        options: {
          // Use SMS instead of WhatsApp
          channel: 'sms',
          // captchaToken: captchaToken
        }
      });

      if (error) {
        console.error("Supabase OTP error details:", error);
        
        // Show specific error messages based on error status and message
        if (error.status === 422) {
          // Parse error message for more details
          if (error.message.includes("Phone number format is invalid")) {
            toast({
              variant: "destructive",
              title: t('auth.invalidPhoneNumber'),
              description: t('auth.phoneNumberFormatError'),
            });
          } else if (error.message.includes("not found")) {
            toast({
              variant: "destructive",
              title: t('auth.phoneNumberNotFound'),
              description: t('auth.phoneNotRegistered'),
            });
          } else if (error.message.includes("rate limit")) {
            toast({
              variant: "destructive",
              title: t('auth.tooManyAttempts'),
              description: t('auth.tooManyVerificationAttempts'),
            });
          } else {
            toast({
              variant: "destructive",
              title: t('auth.verificationError'),
              description: t('auth.phoneVerificationProblem'),
            });
          }
        } else if (error.status === 429) {
          toast({
            variant: "destructive",
            title: t('auth.tooManyRequests'),
            description: t('auth.tooManyVerificationAttempts'),
          });
        } else if (error.status === 400 && error.message.includes("User not found")) {
          toast({
            variant: "destructive",
            title: t('auth.accountNotFound'),
            description: t('auth.noAccountWithPhone'),
          });
        } else {
          toast({
            variant: "destructive",
            title: t('common.error'),
            description: error.message || t('auth.failedToSendCode'),
          });
        }
        // Reset Turnstile on error
        // resetTurnstile();
      } else {
        // Record the OTP request for rate limiting
        recordOTPRequest();
        
        console.log("SMS OTP sent successfully:", data);
        setVerificationSent(true);
        toast({
          title: t('auth.verificationCodeSent'),
          description: t('auth.checkSMSForCode'),
        });
        // Reset Turnstile on success
        // resetTurnstile();
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('auth.unexpectedError'),
      });
      // Reset Turnstile on error
      // resetTurnstile();
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode.trim()) {
      toast({
        variant: "destructive",
        title: t('auth.verificationCodeRequired'),
        description: t('auth.enterVerificationCode'),
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const formattedPhone = formatPhoneNumber(phone, phonePrefix);
      
      console.log("Verifying SMS OTP for phone:", formattedPhone, "with code:", verificationCode);
      
      // Try both verification methods
      try {
        // First try the standard verification
        const { data, error } = await supabase.auth.verifyOtp({
          phone: formattedPhone,
          token: verificationCode,
          type: 'sms',
        });

        if (error) {
          console.error("Standard OTP verification failed:", error);
          // Don't show error yet, we'll try the second method
        } else if (data.user) {
          console.log("Standard verification successful:", data);
          // Show success toast
          toast({
            title: t('auth.welcome'),
            description: t('auth.successfullySignedIn'),
          });
          
          // Log the successful login
          await logUserLogin(data.user.id);
          
          // Redirect user
          try {
            await redirectLoggedInUser(data.user.id);
          } catch (error) {
            console.error("Error redirecting after login:", error);
            // Fallback navigation if profile check fails
            navigate('/dashboard', { replace: true }); 
          }
          return;
        }
      } catch (verifyError) {
        console.error("Standard verification error:", verifyError);
        // Continue to alternate method
      }
      
      // If we get here, try the alternate verification method
      console.log("Trying alternate verification method for sign-in...");
      
      // This is an alternative approach that works in some Supabase configurations
      // when direct OTP verification fails
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        phone: formattedPhone,
        password: verificationCode
      });
      
      if (signInError) {
        console.error("Alternative verification failed:", signInError);
        
        // Provide specific error messages based on error type
        if (signInError.status === 400 && signInError.message.includes("Invalid login credentials")) {
          toast({
            variant: "destructive",
            title: t('auth.invalidVerificationCode'),
            description: t('auth.codeIncorrect'),
          });
        } else if (signInError.message.includes("expired")) {
          toast({
            variant: "destructive",
            title: t('auth.codeExpired'),
            description: t('auth.codeExpiredMessage'),
          });
          
          // Automatically reset to phone entry for expired codes
          setVerificationSent(false);
        } else if (signInError.status === 429) {
          toast({
            variant: "destructive",
            title: t('auth.tooManyAttempts'),
            description: t('auth.tooManyVerificationAttemptsWait'),
          });
        } else if (signInError.status === 404 || signInError.message.includes("User not found")) {
          toast({
            variant: "destructive",
            title: t('auth.accountNotFound'),
            description: t('auth.noAccountFound'),
          });
        } else {
          toast({
            variant: "destructive",
            title: t('auth.verificationFailed'),
            description: t('auth.enterCorrectVerificationCode')
          });
        }
      } else if (signInData.user) {
        console.log("Alternative verification successful:", signInData);
        toast({
          title: t('auth.welcome'),
          description: t('auth.successfullySignedIn'),
        });
        
        // Log the successful login
        await logUserLogin(signInData.user.id);
        
        // Redirect user
        try {
          await redirectLoggedInUser(signInData.user.id);
        } catch (error) {
          console.error("Error redirecting after login:", error);
          // Fallback navigation if profile check fails
          navigate('/dashboard', { replace: true }); 
        }
      } else {
        toast({
          variant: "destructive",
          title: t('auth.verificationFailed'),
          description: t('auth.enterCorrectVerificationCode')
        });
      }
      
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('auth.unexpectedError'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Format phone number using the selected country code
  const formatPhoneNumber = (phoneNumber: string, prefix: string): string => {
    // Clean the phone number by removing all non-digit characters
    let cleaned = phoneNumber.trim().replace(/\D/g, '');
    
    // Remove leading zeros as they're not used in international format
    cleaned = cleaned.replace(/^0+/, '');
    
    // Combine the prefix with the cleaned phone number
    // The prefix should already have the + sign
    return `${prefix}${cleaned}`;
  };

  // Handle input of OTP digits
  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    // Create a copy of the current digits
    const newOtpDigits = [...otpDigits];
    // Update the value at the specified index
    newOtpDigits[index] = value;
    // Update the state
    setOtpDigits(newOtpDigits);
    
    // Update the combined verification code
    setVerificationCode(newOtpDigits.join(''));
    
    // Move to next input if value is entered
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace to move to previous input
  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste event for OTP
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    
    const pastedData = e.clipboardData.getData('text/plain').trim();
    // Check if pasted content is a 6-digit number
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtpDigits(digits);
      setVerificationCode(pastedData);
      // Focus the last input
      inputRefs.current[5]?.focus();
    }
  };

  // Reset OTP fields when going back to phone input
  const handleUseAnotherPhone = () => {
    setVerificationSent(false);
    setOtpDigits(["", "", "", "", "", ""]);
    setVerificationCode("");
  };

  return (
    <div className="space-y-4">
      {!verificationSent ? (
        <form onSubmit={handleSendOTP} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone-signin">{t('auth.phoneNumber')}</Label>
            <div className="flex">
              <Select 
                value={phonePrefix}
                onValueChange={setPhonePrefix}
              >
                <SelectTrigger className="w-[140px] rounded-r-none">
                  <SelectValue placeholder="Select code" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto">
                  <div className="px-2 py-2">
                    <Input
                      placeholder="Search country code..."
                      className="mb-2"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  {filteredPrefixes.length === 0 ? (
                    <div className="px-2 py-4 text-center text-sm text-gray-500">
                      No country codes found
                    </div>
                  ) : (
                    <SelectGroup>
                      {!searchQuery && (
                        <SelectLabel className="px-2 py-1.5 text-xs font-semibold text-gray-500">
                          POPULAR
                        </SelectLabel>
                      )}
                      {filteredPrefixes.map((prefix, index) => {
                        // Show divider after priority countries if not searching
                        const showDivider = !searchQuery && index === 5;
                        
                        return (
                          <div key={prefix.value}>
                            {showDivider && (
                              <SelectLabel className="px-2 py-1.5 text-xs font-semibold text-gray-500 mt-2">
                                ALL COUNTRIES
                              </SelectLabel>
                            )}
                            <SelectItem value={prefix.value}>
                              {prefix.label}
                            </SelectItem>
                          </div>
                        );
                      })}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              <Input
                id="phone-signin"
                type="tel"
                placeholder="712345678"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                className="rounded-l-none placeholder:text-gray-300"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('auth.enterDigitsAfterCountryCode')}
            </p>
          </div>
          {/* <div className="flex justify-center">
            <Turnstile
              key={turnstileKey}
              siteKey={siteKey}
              // onSuccess={(token: any) => setCaptchaToken(token)}
              // onError={() => setCaptchaToken(null)}
            />
          </div> */}
          
          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90" 
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <img 
                  src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                  alt="Loading" 
                  className="h-4 w-4 gebeya-spinner" 
                />
                <span>{t('auth.sendingCode')}</span>
              </div>
            ) : t('auth.signInWithPhone')}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleVerifyOTP} className="space-y-4">
          <Alert variant="default" className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-800">
              {t('auth.verificationNeeded')}
            </AlertTitle>
            <AlertDescription className="text-blue-700">
              {`We've sent a verification code to ${formatPhoneNumber(phone, phonePrefix)} via SMS.`}
              <br />
              <span className="text-sm text-blue-600 mt-1">
                {t('auth.enterSixDigitCode', { action: t('auth.continue') })}
              </span>
            </AlertDescription>
          </Alert>
          
          <div className="space-y-2">
            <Label htmlFor="verification-code-signin">{t('auth.verificationCode')}</Label>
            <div className="flex gap-2 justify-center">
              {otpDigits.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  value={digit}
                  maxLength={1}
                  className="w-12 h-12 text-center text-lg font-semibold"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  aria-label={`Digit ${index + 1}`}
                />
              ))}
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90" 
            disabled={isLoading || verificationCode.length !== 6}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <img 
                  src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                  alt="Loading" 
                  className="h-4 w-4 gebeya-spinner" 
                />
                <span>{t('auth.verifying')}</span>
              </div>
            ) : t('auth.signIn')}
          </Button>
          
          <div className="text-center">
            <button 
              type="button" 
              onClick={handleUseAnotherPhone}
              className="text-sm text-gebeya-pink hover:underline"
            >
              {t('auth.useAnotherPhone')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}; 